import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { wsfTerminalsApiMeta } from "../apiMeta";
import {
  type TerminalLocationsByIdInput,
  terminalLocationsByIdInputSchema,
} from "./shared/terminalLocations.input";
import {
  type TerminalLocation,
  terminalLocationSchema,
} from "./shared/terminalLocations.output";

/**
 * Metadata for the fetchTerminalLocationsByTerminalId endpoint
 */
export const terminalLocationsByTerminalIdMeta = {
  functionName: "fetchTerminalLocationsByTerminalId",
  endpoint: "/terminalLocations/{TerminalID}",
  inputSchema: terminalLocationsByIdInputSchema,
  outputSchema: terminalLocationSchema,
  sampleParams: { TerminalID: 5 },
  endpointDescription:
    "Get location information for a specific terminal by ID.",
} satisfies EndpointMeta<TerminalLocationsByIdInput, TerminalLocation>;

/**
 * Fetch function for retrieving location information for a specific terminal by ID
 */
const { fetch, useHook } = createFetchAndHook<
  TerminalLocationsByIdInput,
  TerminalLocation
>({
  api: wsfTerminalsApiMeta,
  endpoint: terminalLocationsByTerminalIdMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/terminalLocations.endpoints").terminalLocationsGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving location information for a specific terminal by ID
 */
export const fetchTerminalLocationsByTerminalId: FetchFactory<
  TerminalLocationsByIdInput,
  TerminalLocation
> = fetch;

/**
 * React Query hook for retrieving location information for a specific terminal by ID
 */
export const useTerminalLocationsByTerminalId: HookFactory<
  TerminalLocationsByIdInput,
  TerminalLocation
> = useHook;
